/* @jsx h */
import { defineComponent, ref, computed, h, VNode } from "vue";
import { TipsDatum } from "@lemmonhq/types";
import { Loading } from "@app/vue/components/UIComponents";
import { formatPrice } from "@lemmonhq/orders";
import { ViewMode } from "./tips-types";
import { useLocalization } from "@lemmonhq/uikit";
import { useAuth } from "@app/vue/providers/Auth";

type GroupedTipsData = {
  name: string;
  total: number;
  children?: GroupedTipsData[];
  expanded?: boolean;
};

export type TipsSummaryProps = {
  tips: TipsDatum[];
  loading: boolean;
  viewMode: ViewMode;
};

export const getIcon = (viewMode: ViewMode, level: number = 0) => {
  // For top level, icon depends on viewMode
  switch (viewMode) {
    case "user":
      return "person";
    case "table":
      return "table_restaurant";
    case "payment":
      return "payments";
  }
};

export const TipsSummary = defineComponent<TipsSummaryProps>({
  name: "TipsSummary",
  props: ["tips", "loading", "viewMode"],
  setup(props) {
    const auth = useAuth();
    const { t } = useLocalization();
    const expandedGroups = ref<Set<string>>(new Set());

    const toggleExpand = (groupName: string) => {
      if (expandedGroups.value.has(groupName)) {
        expandedGroups.value.delete(groupName);
      } else {
        expandedGroups.value.add(groupName);
      }
    };

    const aggregatedData = computed(() => {
      const data = new Map<string, GroupedTipsData>();
      let grandTotal = 0;

      // First level grouping
      props.tips.forEach((tip) => {
        let key;
        switch (props.viewMode) {
          case "user":
            key = tip.user_name;
            break;
          case "table":
            key = tip.table_name;
            break;
          case "payment":
            key = tip.payment_method;
            break;
        }
        if (key) {
          if (!data.has(key)) {
            data.set(key, { name: key, total: 0, children: [] });
          }
          const amount = Number(tip.total_tips);
          data.get(key)!.total += amount;
          grandTotal += amount;

          // Second level grouping - depends on primary viewMode
          const group = data.get(key)!;
          if (!group.children) group.children = [];

          // Add nested data based on viewMode
          const nestedData = new Map<string, number>();
          props.tips
            .filter((t) => {
              switch (props.viewMode) {
                case "user":
                  return t.user_name === key;
                case "table":
                  return t.table_name === key;
                case "payment":
                  return t.payment_method === key;
              }
            })
            .forEach((t) => {
              // Choose nested grouping based on primary viewMode
              let nestedKey = "";
              switch (props.viewMode) {
                case "user":
                  // When grouped by user, show payment methods
                  nestedKey = t.payment_method || "Unknown";
                  break;
                case "table":
                case "payment":
                  // When grouped by table or payment, show users
                  nestedKey = t.user_name || "Unknown";
                  break;
              }
              nestedData.set(
                nestedKey,
                (nestedData.get(nestedKey) || 0) + Number(t.total_tips),
              );
            });

          group.children = Array.from(nestedData.entries())
            .map(([name, total]) => ({ name, total }))
            .sort((a, b) => b.total - a.total);
        }
      });

      return {
        grandTotal,
        items: Array.from(data.values()).sort((a, b) => b.total - a.total),
      };
    });

    const renderRow = (
      item: GroupedTipsData,
      level: number = 0,
    ): VNode | VNode[] => {
      const isExpanded = expandedGroups.value.has(item.name);
      const hasChildren = item.children && item.children.length > 0;

      return [
        <tr
          key={item.name}
          class={`${
            level === 0 ? "hover uppercase" : "bg-base-200 hover capitalize"
          } cursor-pointer`}
          onClick={() => hasChildren && toggleExpand(item.name)}
        >
          <td class="font-medium">
            <div
              class="flex items-center gap-2 text-accent"
              style={`padding-left: ${(level - 1) * 24}px`}
            >
              {hasChildren && (
                <div class="w-6 flex justify-center">
                  <span class="material-symbols-outlined text-2xl">
                    {isExpanded ? "expand_more" : "chevron_right"}
                  </span>
                </div>
              )}
              {!hasChildren && <div class="w-6" />}
              <span class="material-symbols-outlined">
                {getIcon(props.viewMode, level)}
              </span>
              <span class={level === 0 ? "" : ""}>{item.name}</span>
            </div>
          </td>
          <td class="text-right">
            {formatPrice(
              item.total,
              true,
              2,
              auth.user.value?.restaurant.currency || "CHF",
            )}
          </td>
        </tr>,
        ...(isExpanded && item.children
          ? item.children.map((child) => renderRow(child, level + 1) as VNode)
          : []),
      ];
    };

    return () => {
      if (props.loading) {
        return (
          <div class="w-full h-[200px] flex justify-center items-center">
            <Loading />
          </div>
        );
      }

      const getGroupTitle = () => {
        switch (props.viewMode) {
          case "user":
            return t("reports.tips.staff_member");
          case "table":
            return t("reports.tips.table");
          case "payment":
            return t("reports.tips.payment_method");
        }
      };

      return (
        <div class="space-y-6 flex-1 overflow-y-auto px-2 xl:px-4">
          <div class="w-full rounded-lg bg-base-200 border  shadow">
            <div class=" ">
              <div class=" flex px-2 py-4 space-x-3">
                <div class="">
                  <span class="material-symbols-outlined text-4xl hidden">
                    savings
                  </span>
                </div>
                <div>
                  <div class="stat-title text-base">
                    {" "}
                    {getGroupTitle()} {t("reports.tips.total")}
                  </div>
                  <div class="stat-value text-accent  font-normal">
                    {formatPrice(
                      aggregatedData.value.grandTotal,
                      true,
                      2,
                      auth.user.value?.restaurant.currency || "CHF",
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="card bg-base-200 shadow-xl border ">
            <div class="card-body">
              <h2 class="card-title font-normal text-xl  text-accent flex items-center gap-2 hidden">
                <span class="material-symbols-outlined">
                  {getIcon(props.viewMode)}
                </span>
                {getGroupTitle()}
              </h2>

              <div class="overflow-x-auto">
                <table class="table w-full">
                  <thead>
                    <tr class="text-base-content font-normal text-2xl" style="">
                      <th></th>
                      <th class="text-right font-normal"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {aggregatedData.value.items
                      .filter((el) => el.total > 0)
                      .map((item) => renderRow(item))}
                    {aggregatedData.value.items.length === 0 && (
                      <tr>
                        <td colspan="2" class="text-center ">
                          <div class="flex flex-col items-center gap-2 py-8">
                            <span class="material-symbols-outlined text-4xl">
                              info
                            </span>
                            <span>{t("reports.tips.no_data")}</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      );
    };
  },
});
